/**
 * NCO Page with Server-Side Search
 *
 * This version uses the in-memory search index for fast pagination, sorting, and filtering.
 * Tree data (products) is disabled in this mode - deals are flat.
 *
 * Toggle between this and NCOPage via feature flag or route.
 */

import {
  GridCellParams,
  GridColDef,
  GridColumnVisibilityModel,
  GridDensity,
  GridFilterModel,
  GridRenderCellParams,
  GridRowClassNameParams,
  GridRowId,
  GridRowSelectionModel,
  GridSortModel,
  GridTreeNodeWithRender,
  useGridApiRef,
} from '@mui/x-data-grid-premium'
import useInlineMassUpdate from 'api/aws/useInlineMassUpdate'
import { useUserItems } from 'api/aws/users/useUserItems'
import { useServerSideNco } from 'api/search/useServerSideNco'
import 'components/DataGrid/DataGridContext.css'
import DrawerWrapper from 'components/DataGrid/DrawerWrapper'
import GlobalDataGrid from 'components/DataGrid/GlobalDataGrid'
import GlobalGridToolbar from 'components/DataGrid/GlobalGridToolbar'
import { loadModel, saveModel } from 'components/DataGrid/operators/loadModels'
import Drawer_NCO from 'components/NCO/Drawer_NCO'
import { getNcoCellClassNames } from 'components/NCO/grid/getNcoCellClassNames'
import { getNcoRowClassNames } from 'components/NCO/grid/getNcoRowClassNames'
import { useDrawerContext } from 'context/DrawerProvider/DrawerProvider'
import { useNcoDrawerOpen } from 'context/DrawerProvider/useDrawerState'
import { useLayoutContext } from 'context/LayoutProvider/LayoutProvider'
import { useSnackbarContext } from 'context/SnackbarProvider/SnackbarProvider'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { DealTreeRow, OrganizationTableItem } from 'types'
import { defaultColumnOrder, EDITABLE_FIELDS, ncoAggregationModel } from './defaultModels'
import './grid/NCOPage.css'
import renderNcoColumns from './grid/renderNcoColumns'
import NcoGridFooter from './NcoGridFooter'

const CONTEXT = 'nco'

// Editable fields that should be highlighted when multiple rows are selected

const defaultSortModel: GridSortModel = [
  { field: 'dealStatus', sort: 'desc' },
  { field: 'organizationName', sort: 'asc' },
]

const defaultFilterModel: GridFilterModel = { items: [], quickFilterValues: [] }
const defaultVisibilityModel: GridColumnVisibilityModel = {
  implementationFee: false,
  status: false,
  id: false,
  strategy: false,
  vertical: false,
  stage: false,
  contractId: false,
  type: false,
  startDate: false,
  endDate: false,
  products: false,
  isGoogleDisplay: false,
  isGoogleSearch: false,
  isLinkedIn: false,
  isMetaAdvertising: false,
  isMicrosoftAdvertising: false,
  isSimplifi: false,
  isSnapchat: false,
  isSpotify: false,
  isTikTok: false,
  isYoutube: false,
  isSeo1: false,
  isSeo2: false,
  isSeo3: false,
  isSeo4: false,
  seoTier: false,
  creativeLead: false,
  other: false,
  lastModified: false,
  isBypass: false,
  dateLaunched: false,
  aging: false,
  owner: false,
  __tree_data_group__: false,
}
const pinnedColumns = { left: ['__check__', 'name'], right: [] }

const NCOPageServerSide = () => {
  const { PK, SK } = useParams()
  const { showSnackbar } = useSnackbarContext()
  const apiRef = useGridApiRef()
  const navigate = useNavigate()
  const location = useLocation()
  const { pinned } = useLayoutContext()
  const { openNcoDrawer, closeNcoDrawer } = useDrawerContext()
  const isNcoDrawerOpen = useNcoDrawerOpen()
  const { data: users } = useUserItems()

  const [density, setDensity] = useState<GridDensity>(() => {
    const savedDensity = localStorage.getItem(`$ncoDensityModel`) as GridDensity | null
    return savedDensity ?? 'standard'
  })
  const [selectedDeal, setSelectedDeal] = useState<DealTreeRow | OrganizationTableItem | null>(null)
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>({
    ids: new Set<GridRowId>(),
    type: 'include',
  })
  // Load saved models from localStorage
  const savedSortModel = useMemo(() => loadModel<GridSortModel>(CONTEXT, 'SortModel', defaultSortModel), [])
  const savedFilterModel = useMemo(() => loadModel<GridFilterModel>(CONTEXT, 'FilterModel', defaultFilterModel), [])

  // Use server-side hook
  const {
    rows: serverRows,
    aggregations,
    totalRowCount,
    isLoading,
    error: searchError,
    sortModel,
    onSortModelChange: hookSortModelChange,
    filterModel,
    onFilterModelChange: hookFilterModelChange,
    serverSideProps,
    refresh,
  } = useServerSideNco({
    enabled: true,
    pageSize: 50,
    initialSortModel: savedSortModel,
    initialFilterModel: savedFilterModel,
  })
  const { processRowUpdate, isUpdating } = useInlineMassUpdate<OrganizationTableItem>({
    entityType: 'deal',
    context: CONTEXT,
    rowSelectionModel,
    apiRef,
    editableFields: EDITABLE_FIELDS,
    onMassUpdateComplete: (count, field) => {
      showSnackbar(`Updated "${field}" for ${count} organizations`, 'success')
    },
    resolvers: {
      users: users ?? [],
      // organizations will be passed later via a memo, or we use searchData
    },
  })
  // Wrap change handlers to persist to localStorage
  const onSortModelChange = useCallback(
    (newSortModel: GridSortModel) => {
      saveModel(CONTEXT, 'SortModel', newSortModel)
      hookSortModelChange(newSortModel)
    },
    [hookSortModelChange]
  )

  const onFilterModelChange = useCallback(
    (newFilterModel: GridFilterModel) => {
      saveModel(CONTEXT, 'FilterModel', newFilterModel)
      hookFilterModelChange(newFilterModel)
    },
    [hookFilterModelChange]
  )

  // Process rows to add client-side computed fields
  const processedRows = useMemo(() => {
    if (!serverRows) return []

    const userById = new Map((users ?? []).map(u => [u.user_uuid, u]))
    ;(users ?? []).forEach(u => {
      if (u.id) userById.set(u.id, u)
    })

    return serverRows.map(deal => {
      // Use server-resolved entities when available
      const owner = deal._resolvedOwner || (deal?.owner?.[0] ? userById.get(deal.owner[0]) : undefined)
      const organization = deal._resolvedOrganization || null

      return {
        ...deal,
        owner,
        organization,
        rowId: deal.SK || deal.deal_uuid,
        isParent: true, // All rows are parent deals in flat mode (no tree expansion)
        hasChildRows: false, // Disable tree expansion in server-side mode
        id: deal.deal_uuid || deal.SK,
      }
    })
  }, [serverRows, users])
  // Handle row click
  const handleRowClick = useCallback(
    (deal: DealTreeRow | OrganizationTableItem) => {
      setSelectedDeal(deal)
      openNcoDrawer()

      const { pathname } = location
      if (!/\/ORG_/.test(pathname)) {
        navigate(`/nco/${deal.PK}/${deal.SK}/main`, { replace: true })
        return
      }

      // Replace IDs in path
      let next = pathname.replace(/\/ORG_[^/]+/, `/${deal.PK}`)
      if (deal.SK && /\/DEAL_[^/]+/.test(next)) {
        next = next.replace(/\/DEAL_[^/]+/, `/${deal.SK}`)
      }
      navigate(next, { replace: true })
    },
    [location, navigate, openNcoDrawer]
  )

  const changeDensity = useCallback((newDensity: GridDensity) => {
    setDensity(newDensity)
  }, [])

  const changeRowSelectionModel = useCallback(
    (newSelectionModel: GridRowSelectionModel) => {
      if (newSelectionModel?.ids?.size === 1) {
        const selectedDeal = processedRows.find(d => d?.SK === Array.from(newSelectionModel?.ids)?.[0])
        setSelectedDeal(selectedDeal || null)
      }
    },
    [processedRows]
  )

  // Build columns (without tree toggle since we're flat)
  const toggleExpanded = useCallback(() => {}, []) // No-op in server-side mode
  const columns = useMemo(() => {
    return renderNcoColumns(density, handleRowClick, toggleExpanded, navigate)
  }, [density, handleRowClick, toggleExpanded, navigate]) as GridColDef[]

  // Sync selected deal with URL params
  useEffect(() => {
    if (!SK || !processedRows.length) return
    const selected = processedRows.find(d => d?.SK === SK)
    if (selected) {
      setSelectedDeal(prev => (prev?.SK === selected.SK ? prev : selected))
      if (!isNcoDrawerOpen) openNcoDrawer()
    }
  }, [SK, processedRows, isNcoDrawerOpen, openNcoDrawer])

  const defaultColumnWidths = Object.fromEntries(
    columns.filter(col => typeof col.width === 'number').map(col => [col.field, col.width])
  )

  const getRowClassNameWrapper = (params: GridRowClassNameParams) => getNcoRowClassNames(params, isNcoDrawerOpen)

  // Highlight editable cells when multiple rows are selected (mass update mode)
  const getCellClassNameWrapper = useCallback((params: GridCellParams, rowSelectionModel: GridRowSelectionModel) => {
    const baseClasses = getNcoCellClassNames(params)
    const isMultiSelect = rowSelectionModel?.ids?.size > 1
    const isEditableField = EDITABLE_FIELDS.includes(params.field)

    if (isMultiSelect && isEditableField) {
      return baseClasses ? `${baseClasses} mass-update-target` : 'mass-update-target'
    }
    return baseClasses
  }, [])

  const isCellEditable = (
    params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
    rowSelectionModel: GridRowSelectionModel
  ) => {
    if (!params?.row?.isParent && params.field === 'onboardingStatus') {
      return false
    }
    return true
  }

  const closeDrawer = useCallback(() => {
    closeNcoDrawer()
    setSelectedDeal(null)
    navigate('/nco', { replace: true })
  }, [closeNcoDrawer, navigate])

  const selectedRowId = useMemo(() => {
    return selectedDeal?.SK ?? null
  }, [selectedDeal])

  const isIdsRoute = /^\/nco\/ORG_[^/]+\/DEAL_[^/]+\/[^/]+/i.test(location.pathname)
  const customLoadingMsg = 'Loading New Client Onboards'
  useEffect(() => {
    if (isIdsRoute) openNcoDrawer()
    else closeNcoDrawer()
  }, [isIdsRoute, openNcoDrawer, closeNcoDrawer])

  const FooterComponentWrapper = React.useCallback(
    ({ rowSelectionModel }) => <NcoGridFooter rowSelectionModel={rowSelectionModel} serverSideMode={true} />,
    []
  )

  return (
    <>
      <GlobalDataGrid
        columns={columns}
        rows={processedRows}
        apiRef={apiRef}
        context={'nco'}
        isExpanded={() => false}
        defaultFilterModel={defaultFilterModel}
        defaultVisibilityModel={defaultVisibilityModel}
        defaultSortModel={defaultSortModel}
        defaultGridDensity={'standard'}
        defaultColumnWidths={defaultColumnWidths}
        defaultColumnOrder={defaultColumnOrder}
        pinnedColumns={pinnedColumns}
        aggregationModel={ncoAggregationModel}
        onRowSelectionModelChange={changeRowSelectionModel}
        toolbarComponent={GlobalGridToolbar}
        FooterComponent={FooterComponentWrapper}
        isDrawerOpen={isNcoDrawerOpen}
        onChangeDensity={changeDensity}
        getRowClassName={getRowClassNameWrapper}
        onProcessRowUpdate={processRowUpdate}
        allowInlineMassEdit={true}
        getCellClassName={getCellClassNameWrapper}
        treeData={false} // Disabled in server-side mode
        isCellEditable={isCellEditable}
        // gridSxProps={gridSxProps}
        isPinnedRow={r => (r?.EntityType === 'deal' || r?.isDeal) && r?.onboardingStatus === 'Staging'}
        pinLocation="top"
        selectedRowId={selectedRowId}
        DrawerComponent={DrawerWrapper}
        drawerProps={{
          onRequestClose: closeDrawer,
          context: 'nco',
          selectedRowId,
          apiRef,
          Component: Drawer_NCO,
        }}
        // gridContainerSx={{
        //   position: 'relative',
        //   ...gridContainerStyles,
        //   pt: pinned ? '60px' : 0.8,
        //   pb: 2,
        //   transition: 'all 0.3s ease-in-out',
        //   height: '100%',
        // }}
        // Server-side props
        serverSideMode
        serverAggregations={
          aggregations
            ? {
                total: aggregations.total,
                grossProfit: aggregations.grossProfit,
                spend: aggregations.mediaSpend,
                implementationFee: aggregations.implementationFee,
              }
            : undefined
        }
        {...serverSideProps}
        onSortModelChange={onSortModelChange}
        onFilterModelChange={onFilterModelChange}
        isLoading={isLoading || isUpdating}
      />
    </>
  )
}

export default NCOPageServerSide
